import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, UploadCloud, FileSpreadsheet, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { fmtErr } from "../../lib/api";

const FIELDS = [
  { key: "name", label: "Nome", required: true },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefone" },
  { key: "region", label: "Região" },
  { key: "birthday", label: "Aniversário" },
];

const IGNORE = "__ignore__";

export const ClientsImportModal = ({ onClose, onImported }) => {
  const [step, setStep] = useState(1); // 1 = ficheiro, 2 = mapeamento, 3 = resultado
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [mapping, setMapping] = useState({});
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const inputCls =
    "w-full bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-clay";

  const handleFile = async (file) => {
    if (!file) return;
    const ok = /\.(csv|xlsx|xlsm|txt)$/i.test(file.name);
    if (!ok) return toast.error("Formato não suportado. Use CSV ou Excel (.xlsx).");
    setFileName(file.name);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/clients/import/parse", fd);
      const data = res.data;
      if (!data.total) {
        toast.error("O ficheiro não tem linhas de dados.");
        setParsing(false);
        return;
      }
      setColumns(data.columns || []);
      setRows(data.rows || []);
      setPreview(data.preview || []);
      const m = {};
      FIELDS.forEach((f) => { m[f.key] = (data.mapping && data.mapping[f.key]) || IGNORE; });
      setMapping(m);
      setStep(2);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setParsing(false);
    }
  };

  const runImport = async () => {
    if (!mapping.name || mapping.name === IGNORE) {
      return toast.error("Selecione a coluna que corresponde ao Nome.");
    }
    const cleanMapping = {};
    FIELDS.forEach((f) => {
      cleanMapping[f.key] = mapping[f.key] && mapping[f.key] !== IGNORE ? mapping[f.key] : null;
    });
    setImporting(true);
    try {
      const res = await api.post("/admin/clients/import", {
        mapping: cleanMapping,
        rows,
        skip_duplicates: skipDup,
      });
      setResult(res.data);
      setStep(3);
      onImported && onImported();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      data-testid="clients-import-overlay"
      className="fixed inset-0 z-[85] flex items-start md:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-black/10 p-6 md:p-8 space-y-6 w-full max-w-3xl my-8 animate-pop-in shadow-xl"
        data-testid="clients-import-modal"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button type="button" onClick={() => setStep(1)} className="text-black/40 hover:text-black" aria-label="Voltar">
                <ArrowLeft size={18} />
              </button>
            )}
            <p className="label-editorial">Importar Clientes</p>
          </div>
          <button type="button" onClick={onClose} data-testid="clients-import-close" className="text-black/40 hover:text-black">
            <X size={18} />
          </button>
        </div>

        {/* STEP 1 — Ficheiro */}
        {step === 1 && (
          <div className="space-y-5">
            <p className="text-sm text-black/50 font-light">
              Carregue um ficheiro <strong>CSV</strong> ou <strong>Excel (.xlsx)</strong>. A primeira linha deve conter os
              nomes das colunas. No passo seguinte associa cada coluna aos campos do sistema
              (Nome, Email, Telefone, Região, Aniversário).
            </p>
            <div
              onClick={() => fileRef.current && fileRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-black/15 hover:border-clay cursor-pointer p-10 flex flex-col items-center justify-center text-center transition-colors"
              data-testid="clients-import-dropzone"
            >
              <UploadCloud size={34} className="text-clay mb-3" />
              <p className="text-sm font-light text-black/70">
                {parsing ? "A analisar ficheiro…" : "Clique para escolher ou arraste o ficheiro para aqui"}
              </p>
              <p className="text-xs text-black/40 mt-1">CSV, XLSX · máx. 8MB</p>
              <input
                ref={fileRef}
                data-testid="clients-import-file"
                type="file"
                accept=".csv,.xlsx,.xlsm,.txt"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          </div>
        )}

        {/* STEP 2 — Mapeamento */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm text-black/60">
              <FileSpreadsheet size={16} className="text-clay" />
              <span className="font-light">{fileName}</span>
              <span className="text-black/30">·</span>
              <span className="font-light">{rows.length} linha{rows.length === 1 ? "" : "s"}</span>
            </div>

            <div>
              <p className="label-editorial text-black/50 mb-3">Associar colunas aos campos</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="label-editorial text-black/50 block mb-2">
                      {f.label}{f.required ? " *" : ""}
                    </label>
                    <select
                      data-testid={`import-map-${f.key}`}
                      value={mapping[f.key] || IGNORE}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                      className={inputCls}
                    >
                      <option value={IGNORE}>— Ignorar —</option>
                      {columns.map((c, i) => (
                        <option key={`${c}-${i}`} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {preview.length > 0 && (
              <div>
                <p className="label-editorial text-black/50 mb-3">Pré-visualização</p>
                <div className="border border-black/10 overflow-x-auto max-h-56 overflow-y-auto">
                  <table className="w-full text-xs font-light">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left uppercase tracking-widest text-black/40 border-b border-black/10">
                        {FIELDS.map((f) => (
                          <th key={f.key} className="p-2.5 whitespace-nowrap">{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r, idx) => (
                        <tr key={idx} className="border-b border-black/5 last:border-0">
                          {FIELDS.map((f) => {
                            const col = mapping[f.key];
                            const val = col && col !== IGNORE ? r[col] : "";
                            return <td key={f.key} className="p-2.5 text-black/70 whitespace-nowrap">{val || "—"}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2.5 text-sm font-light text-black/70 cursor-pointer">
              <input
                type="checkbox"
                data-testid="import-skip-dup"
                checked={skipDup}
                onChange={(e) => setSkipDup(e.target.checked)}
                className="accent-clay w-4 h-4"
              />
              Ignorar duplicados (mesmo telefone, email ou nome de clientes já existentes)
            </label>

            <div className="flex items-center gap-4 pt-1">
              <div className="flex-1" />
              <button type="button" onClick={() => setStep(1)} className="text-sm text-black/50 hover:text-black">Voltar</button>
              <button
                data-testid="import-run-btn"
                onClick={runImport}
                disabled={importing}
                className="label-editorial bg-clay text-white px-8 py-3 hover:bg-clay-dark transition-colors disabled:opacity-50"
              >
                {importing ? "A importar…" : `Importar ${rows.length} cliente${rows.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Resultado */}
        {step === 3 && result && (
          <div className="space-y-6" data-testid="import-result">
            <div className="flex flex-col items-center text-center py-4">
              <CheckCircle2 size={44} className="text-green-600 mb-3" />
              <p className="text-lg font-light">Importação concluída</p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="border border-black/10 p-4">
                <p className="text-2xl font-light text-green-700">{result.imported}</p>
                <p className="text-xs uppercase tracking-widest text-black/40 mt-1">Importados</p>
              </div>
              <div className="border border-black/10 p-4">
                <p className="text-2xl font-light text-black/60">{result.skipped}</p>
                <p className="text-xs uppercase tracking-widest text-black/40 mt-1">Ignorados</p>
              </div>
              <div className="border border-black/10 p-4">
                <p className="text-2xl font-light text-red-600">{result.errors}</p>
                <p className="text-xs uppercase tracking-widest text-black/40 mt-1">Com erro</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                data-testid="import-done-btn"
                onClick={onClose}
                className="label-editorial bg-clay text-white px-8 py-3 hover:bg-clay-dark transition-colors"
              >
                Concluir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsImportModal;
